import type { FlotiqStore } from "@jti/fe-common";

import type { StoreFiltersCategoriesEnabledParams, StoreLocatorProps } from "../../types";
import type { GetFlotiqStores, GetFlotiqStoresReturnType } from "../useFlotiqStores/useFlotiqStores.types";
import type { UseStoreFiltersReturnType } from "../useStoreFilters/useStoreFilters.types";

export type StoreLocatorProviderProps = Pick<
  StoreLocatorProps,
  | "defaultLat"
  | "defaultLng"
  | "defaultZoom"
  | "isMapPositionFromUrlEnabled"
  | "storeDetailsPage"
  | "activeStoreId"
  | "flotiqApiKey"
  | "flotiqContentType"
  | "defaultStoreIcon"
  | "storeIcons"
  | "isSearchVisible"
  | "isStoreCardAddressEnabled"
  | "isOpeningHoursEnabled"
  | "isOpeningHoursBreaksEnabled"
  | "isAvailableSticksEnabled"
  | "isMultiBrandSticksEnabled"
  | "isAvailableDevicesEnabled"
  | "isMapInteractionEnabled"
  | "isGeneralAnnouncementEnabled"
  | "isAvailableServicesEnabled"
  | "isStoreTypeEnabled"
  | "isVendingMachineEnabled"
  | "isVendingMachineTipEnabled"
  | "isMultipleFiltersSectionOpenEnabled"
  | "defaultOpenedFilterCategory"
  | "isInputExtraTextVisible"
  | "isAsianAddressFormatEnabled"
> & {
  handleSelectStore: (storeId: string) => void;
  handleLoadFlotiqStores: GetFlotiqStores;
  isStoreDetails?: boolean;
  onActivity?: () => void;
  onTap?: () => void;
  isNoStoresFilters?: { value: boolean; type: string } | null;
  activeStoreIcon?: string;
  mapTheme?: "light" | "dark";
};

export type StoreLocatorCommonApi = {
  mapRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  isMapLoading: boolean;
  isMapError: boolean;
  isSearchResultPending?: boolean;
  handleInitMap: () => void;
  handleInputSearchClick: () => void;
  handleLoadMarkers: (stores: FlotiqStore[]) => void;
  handleLoadUserMarker: (lat: number, lng: number) => void;
  handleSelectStoreFromList?: (storeId: string) => void;
  isMultipleFiltersSectionOpenEnabled?: boolean;
  defaultOpenedFilterCategory?: string;
  isStoreCardAddressEnabled?: boolean;
  isOpeningHoursEnabled?: boolean;
  isOpeningHoursBreaksEnabled?: boolean;
  isAvailableSticksEnabled?: boolean;
  isMultiBrandSticksEnabled?: boolean;
  isAvailableDevicesEnabled?: boolean;
  isOpenNowFilterEnabled?: boolean;
  isGeneralAnnouncementEnabled?: boolean;
  isAvailableServicesEnabled?: boolean;
  isStoreTypeEnabled?: boolean;
  isVendingMachineEnabled?: boolean;
  isVendingMachineTipEnabled?: boolean;
  isNoStoresSearch?: { value: boolean; type: string } | null;
  handleMapExpansion?: () => void;
  isInputExtraTextVisible?: boolean;
  isNearbyShopsEnabled?: boolean;
  isAsianAddressFormatEnabled?: boolean;
  defaultZoom?: number;
};

export type GoogleStoreLocatorApi = StoreLocatorCommonApi & { renderInfoWindow?: () => React.ReactElement | null };

export type StoreLocatorReturnType = GetFlotiqStoresReturnType &
  GoogleStoreLocatorApi &
  StoreFiltersCategoriesEnabledParams &
  UseStoreFiltersReturnType & {
    storeDetailsPage?: string;
    storeDetailsPageTarget?: "_blank" | "_self";
    filtersEnabled?: boolean;
    isSearchVisible?: boolean;
    isMapInteractionEnabled?: boolean;
    onChooseStore: StoreLocatorProps["onChooseStore"];
    isMapActive: boolean;
    translationPrefix?: string;
  };

export type UseStoreLocator = (props: StoreLocatorProps & { activeStore?: FlotiqStore }) => StoreLocatorReturnType;
